import "./Profile.css"

import { ErrorMessage, Form, Formik, useFormikContext } from "formik"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import Button from "@/components/Button/Button"
import Skills from "@/features/pages/Profile/Skills/Skills"
import SocialLinks from "@/features/pages/Profile/SocialLinks/SocialLinks"
import PicImg from "@/features/pages/Profile/UserProfileForm/PicImg"
import UserProfileForm from "@/features/pages/Profile/UserProfileForm/UserProfileForm"
import useFormErr from "@/hooks/useFormErr"
import { useEditStudentMutation } from "@/queries/mutations/studentMutations"
import { useProfileData } from "@/queries/queries"
import { editStudentSchema } from "@/schemas/HomeSchema"
import type { StudentProfile, StudentWithPassword } from "@/types/profileTypes"
import { checkProfileComplete } from "@/utils/helpers"

export default function Profile() {
  const [uploading, setUploading] = useState(false)

  const { data: profile } = useProfileData()

  const editStudentMutation = useEditStudentMutation()

  const initialValues = useInitialProfileValues(profile)

  const isProfileComplete = checkProfileComplete(profile)

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={editStudentSchema}
        enableReinitialize
        onSubmit={(values) => {
          const final = {
            ...values,
            gov: values.country !== "Egypt" ? "other" : values.gov,
            college: values.country !== "Egypt" ? "other" : values.college,
            image: typeof values.image === "object" ? values.image : "",
          }

          const isNewPhoto = typeof values.image === "object"
          if (isNewPhoto) setUploading(true)

          editStudentMutation.mutate(final, {
            onSettled: () => setUploading(false),
          })
        }}
      >
        {() => {
          return (
            <>
              {!isProfileComplete && (
                <div
                  className="d-flex flex-column gap-2 text-center mb-2"
                  style={{ fontSize: "16px" }}
                >
                  <p dir="ltr" className="bg-info-subtle rounded-3 p-3">
                    {/* eslint-disable-line i18next/no-literal-string  */}
                    🌟 Complete your profile and let&apos;s get to know you
                    better, so we can help you find your first job 🚀
                  </p>
                  <p dir="rtl" className="bg-info-subtle rounded-3 p-3">
                    {/* eslint-disable-line i18next/no-literal-string  */}
                    🌟 كمّل بروفايلك وخلينا نتعرف عليك أكتر دي عشان نقدر نساعدك
                    تلاقي اول وظيفة ليك 🚀
                  </p>
                </div>
              )}
              <Inputs
                isLoading={editStudentMutation.isPending}
                uploading={uploading}
                initialValues={initialValues}
              />
            </>
          )
        }}
      </Formik>
    </>
  )
}

interface InputsProps {
  uploading: boolean
  initialValues: StudentProfile
  isLoading: boolean
}

function Inputs({ uploading, initialValues, isLoading }: InputsProps) {
  const { values, setFieldValue, submitForm } =
    useFormikContext<StudentProfile>()
  const { t } = useTranslation()
  const { formErr } = useFormErr<StudentProfile>()

  useEffect(() => {
    if (
      values.image &&
      typeof values.image === "object" &&
      values.image !== initialValues.image
    ) {
      submitForm()
    }
  }, [values.image])

  return (
    <Form className="profile">
      <div className="d-flex flex-column gap-3 ">
        <div className="bg-white info d-flex align-items-center flex-column flex-md-row flex-row gap-3">
          <div className="position-relative d-inline-block">
            <PicImg
              student
              values={values}
              name="image"
              setFieldValue={setFieldValue}
              oldFile={""}
              backError={formErr?.image}
              error={<ErrorMessage name="image" />}
            />
            {uploading && (
              <div className="loading-overlay position-absolute top-50 start-50 translate-middle">
                <div
                  className="spinner-border text-primary"
                  role="status"
                  style={{ width: "1.5rem", height: "1.5rem" }}
                >
                  <span className="visually-hidden"></span>
                </div>
              </div>
            )}
          </div>

          <div className="formation">
            <h3 className="fs-5 fw-bold">{initialValues?.name}</h3>
            <p className="fs-6 ">{initialValues?.email}</p>
          </div>
        </div>

        <div className="form bg-white">
          <div className="d-flex justify-content-between align-items-center">
            <h3>{t("profilePage.title")}</h3>
          </div>

          <UserProfileForm />
          <div className="d-flex justify-content-end">
            <Button isLoading={isLoading} type="submit">
              {t("buttons.save")}
            </Button>
          </div>
        </div>
        <SocialLinks />
        <Skills />
      </div>
    </Form>
  )
}

function useInitialProfileValues(profile?: StudentProfile) {
  const { t } = useTranslation()
  return useMemo<StudentWithPassword>(() => {
    if (!profile) return {} as StudentWithPassword
    else
      return {
        ...profile,
        name: profile?.name,
        email: profile?.email,
        phone: profile?.phone,
        image: profile?.image || "../../../public/assets/images/avatar.png",
        birthdate: profile?.birthdate || "",
        country: profile?.country || t("profilePage.defaults.country"),
        gov: profile?.gov || "",
        college: profile?.college,
        profession_id: profile?.profession_id,
        student_status: profile?.student_status || 0,
        password: "",
        password_confirmation: "",
        army_status: profile?.army_status || "",
      }
  }, [profile])
}
